import { useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import {
  useIncidents,
  useSalesFilters,
  useSalesQuarterly,
  useSalesSeries,
  useSalesSummary,
  useSalesTimeline,
  useServiceOverview,
} from '@/hooks/useDeepDive'
import {
  defaultFilterValues,
  toFilterGroups,
  toFilterSelection,
  toFreshness,
  toHealthTiles,
  toIncidents,
  toQuarterPoints,
  coverageNote,
  toPeriodSalesTile,
  toPeriodTiles,
  toSeriesPoints,
  toTimelinePoints,
} from '@/lib/deep-dive-adapters'

import { ALL_OPTION, type SystemPageConfig } from '@/lib/system-intelligence'
import type { SalesPeriodSelection } from '@/lib/api/deep-dive.types'
import { PeriodPicker } from './PeriodPicker'
import { StatTile } from './StatTile'
import { SystemFilterBar } from './SystemFilterBar'
import { QuarterlyPerformance } from './QuarterlyPerformance'
import { SalesTimeline } from './SalesTimeline'
import { IncidentHistoryTable } from './IncidentHistoryTable'
import { ServiceOutageHeader } from './ServiceOutageHeader'
import { PanelError } from './PanelState'

/**
 * The shared drill-down layout: scope row, the health tiles, then one period
 * selector governing the sales tiles, the quarterly roll-up and the timeline
 * beside it, and finally the downtime log.
 *
 * Aurora, Solo and One Portal are the same screen against different services, so
 * both the layout and the six queries behind it live here once and each page
 * passes a `SystemPageConfig`. The API returns the same shape for all three
 * platforms by design, which is what makes one component possible.
 *
 * Each panel owns its own loading and failure state rather than the page having
 * one. That is deliberate: the health endpoints are local and always answer,
 * while the sales ones sit in front of OWEHub and can time out on their own — a
 * single page-level spinner would hide working uptime figures behind a slow
 * sales query, and a single error state would blank the screen over one panel.
 *
 * The trail back and the UTC clock are in the header for these routes (see
 * `breadcrumb`/`showClock` in config/navigation.ts), so the page opens on its
 * title.
 */
export function SystemIntelligenceView({
  service,
  title,
  unit,
  compact,
  salesTodayHeader,
}: SystemPageConfig) {
  const filtersQuery = useSalesFilters(service)
  const groups = useMemo(() => toFilterGroups(filtersQuery.data), [filtersQuery.data])

  /**
   * Chip id → chosen value. Seeded lazily and reconciled against the catalogue
   * below, since the dimensions are not known until it arrives.
   */
  const [chosen, setChosen] = useState<Record<string, string>>({})
  /**
   * The period the headline figures and the series chart cover.
   *
   * This replaces a client-side fan-out that fetched one day at a time and
   * summed them, which is what the screen had to do before `sales/summary`
   * and `sales/series` existed. Seven requests became one, and periods the
   * fan-out could never have afforded — a year, by month — are now free.
   */
  const [period, setPeriod] = useState<SalesPeriodSelection>({ key: 'today' })

  const selection = useMemo(() => toFilterSelection(chosen), [chosen])
  const dirty = useMemo(
    () => groups.some((g) => (chosen[g.id] ?? ALL_OPTION) !== ALL_OPTION),
    [groups, chosen],
  )
  /** Every dimension the catalogue offers, defaulted to "All". */
  const values = useMemo(() => ({ ...defaultFilterValues(groups), ...chosen }), [groups, chosen])

  const overview = useServiceOverview(service)
  const incidents = useIncidents(service)

  /**
   * One summary and one series per period change, both server-aggregated.
   *
   * The period's dates, its comparison window and the chart's bucket size are
   * all the server's answers — the client sends a period name and reads what
   * comes back. Deriving any of them here is how a chart ends up labelled for a
   * span it is not showing.
   */
  const summary = useSalesSummary(service, period, selection)
  const series = useSalesSeries(service, period, selection)

  /**
   * Everything dated on this screen reads off the one period.
   *
   * The hourly panel and the quarterly roll-up used to carry a date picker
   * and a year picker of their own, which put three date controls on one
   * page and let them disagree — a tile saying "today" above a chart showing
   * last Tuesday. They are derived now, so there is nothing to keep in sync.
   */
  const periodWindow = summary.data?.period ?? series.data?.period
  /** The single day the hourly panel shows; only meaningful at hour granularity. */
  const hourlyDate = periodWindow?.granularity === 'hour' ? periodWindow.from_date : undefined
  const periodYear = periodWindow ? Number(periodWindow.from_date.slice(0, 4)) : undefined

  const timeline = useSalesTimeline(service, selection, hourlyDate)
  const quarterly = useSalesQuarterly(service, selection, periodYear)
  /** Shown beside the chart when the source starts inside the chosen period. */
  const coverage = coverageNote(series.data?.coverage ?? summary.data?.coverage)

  const openIncident = overview.data?.open_incident ?? null

  /**
   * The header row: service health, and on the three-tile pages the chosen
   * period's sales beside it.
   *
   * The sales tile follows the period selector above it — "This week" retitles
   * it and shows the week — which is why the row sits below the scope row on
   * those pages rather than above it.
   */
  const headerTiles = [
    ...toHealthTiles(overview.data, incidents.data?.total),
    ...(salesTodayHeader
      ? [toPeriodSalesTile(summary.data, unit, period, summary.isLoading)]
      : []),
  ]

  /* Health, and today's sales on the pages that ask for it.
     This is the one failure the page cannot route into a panel — a tile is too
     small to explain itself. Sales failures stay in their own tiles as "n/a". */
  const tileRow = overview.isError ? (
    <PanelError
      error={overview.error}
      onRetry={() => void overview.refetch()}
      className="border-line bg-surface rounded-lg border"
    />
  ) : (
    <div
      className={cn(
        'grid sm:grid-cols-2',
        // Three tiles want a third column of their own rather than a
        // 2 + 1 row with a gap where the fourth tile used to be.
        salesTodayHeader && 'lg:grid-cols-3',
        compact ? 'gap-3' : 'gap-4',
      )}
    >
      {headerTiles.map((stat) => (
        <StatTile key={stat.id} stat={stat} compact={compact} />
      ))}
    </div>
  )

  /* One controls row for the sales half of the screen: scope chips and the
     single period. The page used to carry three date controls — a day picker
     here, a period band of its own, and a year picker on the quarterly panel —
     which could disagree with each other, and did. */
  const filterRow = (
    <SystemFilterBar
      groups={groups}
      value={values}
      dirty={dirty}
      isLoading={filtersQuery.isLoading}
      error={filtersQuery.error}
      onChange={(id, option) => setChosen((current) => ({ ...current, [id]: option }))}
      onClear={() => setChosen({})}
      compact={compact}
      trailing={
        /* The dates the period resolved to are not repeated here: the timeline
           heading below already prints them for every period, and a second copy
           on this row wrapped it onto two lines. The title carries them for a
           reader who wants them without looking down. */
        <PeriodPicker
          value={period}
          onChange={setPeriod}
          compact={compact}
          title={
            summary.data &&
            `${summary.data.period.from_date} → ${summary.data.period.to_date}${
              summary.data.period.in_progress ? ' · in progress' : ''
            }`
          }
        />
      }
    />
  )

  return (
    <div className={cn(compact ? 'space-y-3 pb-1' : 'space-y-6 pb-4')}>
      {/* A system that is down leads with how long for; otherwise the title
          carries the row on its own. Both render the heading at 32px on desktop. */}
      {openIncident ? (
        <ServiceOutageHeader
          title={title}
          downSince={new Date(openIncident.started_at)}
          note={`${openIncident.indicator} · ${openIncident.to_status.replace('_', ' ')}`}
        />
      ) : (
        <h1 className="text-fg text-3xl leading-tight font-semibold tracking-tight sm:text-[32px]">
          {title}
        </h1>
      )}

      {/* The three-tile pages lead with the controls and put the tiles under
          them, so
          the scope the figures were read under is stated before them. On the
          default layout health leads instead, because response time and uptime
          are service-wide and below the row would read as figures that moved
          when you picked a different office, or last year. */}
      {salesTodayHeader ? (
        <>
          {filterRow}
          {tileRow}
        </>
      ) : (
        <>
          {tileRow}
          {filterRow}
        </>
      )}

      {/* The period summary tiles. Pages with the three-tile header drop them
          entirely — the period still drives the timeline and the quarterly
          roll-up below, and the summary call still feeds the timeline's
          headline and the picker's tooltip. */}
      {salesTodayHeader ? null : summary.isError ? (
        <PanelError
          error={summary.error}
          onRetry={() => void summary.refetch()}
          className="border-line bg-surface rounded-lg border"
        />
      ) : (
        <div className={cn('grid sm:grid-cols-2 xl:grid-cols-4', compact ? 'gap-3' : 'gap-4')}>
          {toPeriodTiles(summary.data, unit).map((stat) => (
            <StatTile key={stat.id} stat={stat} compact={compact} />
          ))}
        </div>
      )}

      {/* The quarterly roll-up is a reference figure, the timeline is the screen's
          subject — hence one column against two on a wide viewport. */}
      <div className={cn('grid xl:grid-cols-3', compact ? 'gap-3' : 'gap-4')}>
        <QuarterlyPerformance
          data={toQuarterPoints(quarterly.data?.quarters)}
          year={quarterly.data?.year}
          freshness={quarterly.data && toFreshness(quarterly.data)}
          isLoading={quarterly.isLoading}
          error={quarterly.error}
          onRetry={() => void quarterly.refetch()}
          compact={compact}
        />
        <div className="xl:col-span-2">
          {/* Today keeps the original hourly panel, because only it carries the
              30-day weekday baseline — the series endpoint has no equivalent, and
              that comparison is the whole point of the day view. Every wider
              period uses the series, bucketed however the server chose. */}
          {period.key !== 'today' ? (
            <SalesTimeline
              data={toSeriesPoints(series.data)}
              title={`Sales Intelligence Timeline (${series.data?.period.from_date ?? '…'} → ${
                series.data?.period.to_date ?? '…'
              })`}
              headline={{
                currentHourSales: summary.data?.sales.value ?? null,
                baselineThisHour: summary.data?.daily_avg ?? null,
              }}
              headlineLabels={{ current: 'Total', baseline: 'Avg Day' }}
              unitWord={
                series.data?.period.granularity === 'month'
                  ? 'this month'
                  : series.data?.period.granularity === 'hour'
                    ? 'this hour'
                    : 'this day'
              }
              note={coverage ?? undefined}
              isLoading={series.isLoading}
              error={series.error}
              onRetry={() => void series.refetch()}
              compact={compact}
            />
          ) : (
            <SalesTimeline
              data={toTimelinePoints(timeline.data)}
              headline={{
                currentHourSales: timeline.data?.current_hour_sales ?? null,
                baselineThisHour: timeline.data?.baseline_this_hour ?? null,
              }}
              date={timeline.data?.date}
              timezone={timeline.data?.timezone}
              baselineDays={timeline.data?.baseline_days}
              isToday={period.key === 'today'}
              freshness={timeline.data && toFreshness(timeline.data)}
              isLoading={timeline.isLoading}
              error={timeline.error}
              onRetry={() => void timeline.refetch()}
              compact={compact}
            />
          )}
        </div>
      </div>

      <IncidentHistoryTable
        incidents={toIncidents(incidents.data?.incidents)}
        total={incidents.data?.total}
        isLoading={incidents.isLoading}
        error={incidents.error}
        onRetry={() => void incidents.refetch()}
        compact={compact}
      />
    </div>
  )
}
